'use client';
import React, { useEffect, useState } from 'react';

import {
  filterServiceByCategory,
  filterTenantByCategory,
  getAllTilesList,
  getCategoryInformationForServices,
  getCategoryInformationForTenants,
  getInitialFilterTiles,
} from '@/helpers/floor-plan/floor-plan-helpers';
import { IMeetingPlacePageFloorPlan } from '@/lib/fragments/meetingPlacePageFloorPlan';
import { IMeetingplacePageHome } from '@/lib/fragments/meetingPlacePageHome';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import { TTenantTypes } from '@/lib/fragments/meetingPlacePageTenantList';
import { FloorPlanCategoriesData } from '@/templates/meeting-place/page/floorplan/meeting-place-page-floorplan';
import { FloorPlanFilterListStyle } from '../floor-plan-filter/floor-plan-filter-list-style.type';
import { FloorPlanFilterListType } from '../floor-plan-filter/floor-plan-filter-list-type.type';
import { FloorPlanMap } from '../floor-plan-map/floor-plan-map';
import { FilterTile } from '../floor-plan-map/floor-plan-map.interface';
import { DictionarySubDirectories } from '@/helpers/rendered-page-creation/get-url-subdirectories';

interface FloorPlanProps {
  mapHash: string;
  page: IMeetingPlacePageFloorPlan;
  node_locale: string;
  tenantShopList?: TTenantTypes[];
  tenantFbList?: TTenantTypes[];
  tenantEntertainmentList?: TTenantTypes[];
  serviceList?: IMeetingPlacePageServiceItem[];
  categoryData: FloorPlanCategoriesData;
  homepage: IMeetingplacePageHome;
  config: any;
  urlSubdirectories: DictionarySubDirectories[];
}

export interface ClickHistory {
  title: string;
  type: FloorPlanFilterListType;
}

export const FloorPlan = ({
  mapHash,
  page,
  node_locale,
  tenantShopList,
  serviceList,
  tenantEntertainmentList,
  tenantFbList,
  categoryData,
  homepage,
  config,
  urlSubdirectories,
}: FloorPlanProps) => {
  //Filter
  const [allTilesList] = useState<FilterTile[]>(
    getAllTilesList(
      config,
      urlSubdirectories,
      tenantShopList,
      tenantFbList,
      tenantEntertainmentList,
      serviceList,
    ),
  );

  const [markedPlaces, setMarkedPlaces] = useState<FilterTile[]>([]);

  const [filterListStyle, setFilterListStyle] =
    useState<FloorPlanFilterListStyle>('tile');

  const [filterList, setFilterList] = useState<FilterTile[]>(
    getInitialFilterTiles(
      categoryData,
      {
        shop: tenantShopList,
        fb: tenantFbList,
        entertainment: tenantEntertainmentList,
        service: serviceList,
      },
      page,
      config,
    ),
  );

  const [filterListType, setFilterListType] =
    useState<FloorPlanFilterListType>('start');

  const [clickHistory, setClickHistory] = useState<ClickHistory[]>([]);

  useEffect(() => {
    switch (filterListType) {
      case 'start':
        setFilterListStyle('tile');
        setFilterList(() =>
          getInitialFilterTiles(
            categoryData,
            {
              shop: tenantShopList,
              fb: tenantFbList,
              entertainment: tenantEntertainmentList,
              service: serviceList,
            },
            page,
            config,
          ),
        );
        break;
      case 'entertainment':
        setFilterListStyle('category');
        setFilterList([
          {
            categories: getCategoryInformationForTenants(
              tenantEntertainmentList,
            ),
            type: 'entertainment',
            title: '',
          },
        ]);
        break;
      case 'fb':
        setFilterListStyle('category');
        setFilterList([
          {
            categories: getCategoryInformationForTenants(tenantFbList),
            title: '',
            type: 'fb',
          },
        ]);
        break;
      case 'shop':
        setFilterListStyle('category');
        setFilterList([
          {
            categories: getCategoryInformationForTenants(tenantShopList),
            title: '',
            type: 'shop',
          },
        ]);
        break;
      case 'service':
        setFilterListStyle('category');
        setFilterList([
          {
            categories: getCategoryInformationForServices(serviceList, config),
            title: '',
            type: 'service',
          },
        ]);
        break;
      case 'search':
        setFilterListStyle('tile');
        setFilterList(filterList);
        break;
      case 'showAll':
        setFilterList(allTilesList);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterListType]);

  const filterByCategory = (
    filterCategory: string,
    type?: FloorPlanFilterListType,
  ) => {
    if (filterCategory === 'showAll') {
      setFilterListType('showAll');
    } else if (filterCategory !== '' && type) {
      let filteredList;
      setFilterListType('search');

      switch (type) {
        case 'all':
          filteredList = {
            shop: tenantShopList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            ),
            fb: tenantFbList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            ),
            entertainment: tenantEntertainmentList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            ),
            service: serviceList?.filter((service) =>
              filterServiceByCategory(service, filterCategory, config),
            ),
          };
          setFilterList(
            getAllTilesList(
              config,
              urlSubdirectories,
              filteredList.shop,
              filteredList.fb,
              filteredList.entertainment,
              filteredList.service,
            ),
          );
          break;

        case 'shop':
          filteredList = tenantShopList;
          if (filterCategory !== 'all') {
            filteredList = tenantShopList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            );
          }
          setFilterList(
            getAllTilesList(
              config,
              urlSubdirectories,
              filteredList,
              undefined,
              undefined,
              undefined,
            ),
          );
          break;

        case 'entertainment':
          filteredList = tenantEntertainmentList;
          if (filterCategory !== 'all') {
            filteredList = tenantEntertainmentList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            );
          }

          setFilterList(
            getAllTilesList(
              config,
              urlSubdirectories,
              undefined,
              undefined,
              filteredList,
              undefined,
            ),
          );
          break;

        case 'fb':
          filteredList = tenantFbList;
          if (filterCategory !== 'all') {
            filteredList = tenantFbList?.filter((tenant) =>
              filterTenantByCategory(tenant, filterCategory),
            );
          }
          setFilterList(
            getAllTilesList(
              config,
              urlSubdirectories,
              undefined,
              filteredList,
              undefined,
              undefined,
            ),
          );
          break;

        case 'service':
          filteredList = serviceList;
          if (filterCategory !== 'all') {
            filteredList = serviceList?.filter((service) =>
              filterServiceByCategory(service, filterCategory, config),
            );
          }
          setFilterList(
            getAllTilesList(
              config,
              urlSubdirectories,
              undefined,
              undefined,
              undefined,
              filteredList,
            ),
          );
          break;
      }
    } else {
      setFilterListType('start');
    }
  };

  const filter = (filterValue: string) => {
    if (filterValue !== '') {
      const filteredList = allTilesList.filter((tile) => {
        return tile.title
          ?.toLocaleLowerCase()
          .includes(filterValue.toLocaleLowerCase());
      });
      setFilterListType('search');
      setFilterList(filteredList);
    } else {
      setFilterListType('start');
    }
  };

  const tenants = allTilesList.filter((tile) =>
    tile.floorPlanIDs?.some((id) => id !== null) ||
    tile?.unitId?.some((id) => id !== null),
  );

  return (
    <div
      className="relative md:rounded-xxl"
      style={{ borderRadius: 40 }}
    >
      <FloorPlanMap
        listType={filterListType}
        mapHash={mapHash}
        node_locale={node_locale}
        listStyle={filterListStyle}
        activeTiles={filterList}
        allTiles={tenants}
        performListFiltering={filter}
        performCategoryListing={filterByCategory}
        updateFilterList={setFilterListType}
        setClickHistory={setClickHistory}
        clickHistory={clickHistory}
        markedPlaces={markedPlaces}
        setMarkedPlaces={setMarkedPlaces}
        homepage={homepage}
      />
    </div>
  );
};
